import { jsxs, jsx } from "react/jsx-runtime";
import { useMemo } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { SubmitButton } from "../shared/submit-button.js";
import { useTranslation } from "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { DifficultyBadge, DebugQuizId } from "../shared/debug-quiz-id.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useColoringQuiz } from "../hooks/use-coloring-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function ColoringStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel
}) {
  const { t } = useTranslation("quiz");
  const {
    answers,
    isSolved,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    digits,
    inverted,
    hasImages,
    correctColorCount,
    itemsPerRow,
    rowIndices,
    coloredCount,
    handleToggle,
    handleSubmit,
    getImageSrc,
    getIsColored
  } = useColoringQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const feedbackText = useMemo(() => {
    if (!isSolved) return "";
    if (shouldShowCorrectAnswers && correctColorCount !== null) {
      const count2 = inverted ? digits - correctColorCount : correctColorCount;
      return inverted ? t("types.coloring.correctAnswerErase", { count: count2 }) : t("types.coloring.correctAnswerColor", { count: count2 });
    }
    const count = inverted ? answers.filter((a) => a === 0).length : coloredCount;
    return inverted ? t("types.coloring.erased", { count }) : t("types.coloring.colored", { count });
  }, [isSolved, shouldShowCorrectAnswers, correctColorCount, inverted, digits, answers, coloredCount, t]);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center", children: [
          /* @__PURE__ */ jsx("div", { className: "inline-flex gap-2 items-center px-3 py-1 text-sm font-bold text-white bg-[#55C3FF] rounded-lg", children: /* @__PURE__ */ jsx("span", { children: t("types.coloring.label") }) }),
          /* @__PURE__ */ jsx(DifficultyBadge, { level: difficultyLevel }),
          /* @__PURE__ */ jsx(DebugQuizId, { quizId })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        className: cn(
          "p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50",
          "border border-gray-100 shadow-2xs",
          "text-2xl font-medium leading-relaxed text-gray-900"
        ),
        children: [
          /* @__PURE__ */ jsx(KatexText, { text: quiz.title }),
          quiz.problem && (() => {
            const cleanProblem = quiz.problem.replace(/\{\{\}\}/g, "").trim();
            if (!cleanProblem) return null;
            return /* @__PURE__ */ jsx("div", { className: "mt-4 text-xl", children: formatText(cleanProblem, `${quizId}-problem`, quiz.additionalImages ?? void 0) });
          })(),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col items-center gap-2 py-4", children: rowIndices.map((rowItems, rowIndex) => /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center gap-2", children: rowItems.map((itemIndex) => {
      const isColored = getIsColored(itemIndex);
      return /* @__PURE__ */ jsx(
        motion.button,
        {
          initial: { opacity: 0, scale: 0.8 },
          animate: { opacity: 1, scale: 1 },
          transition: { delay: itemIndex * 0.03 },
          whileHover: !isSolved && !isViewSolved ? { scale: 1.1 } : void 0,
          whileTap: !isSolved && !isViewSolved ? { scale: 0.95 } : void 0,
          onClick: () => handleToggle(itemIndex),
          disabled: isSolved || isViewSolved,
          className: cn(
            "relative cursor-pointer select-none",
            (isSolved || isViewSolved) && "pointer-events-none"
          ),
          children: hasImages ? /* @__PURE__ */ jsx(
            "img",
            {
              src: getImageSrc(itemIndex),
              alt: `item-${itemIndex}`,
              className: "w-auto h-auto hover:opacity-50 transition-opacity",
              style: { maxWidth: `${Math.min(80, Math.floor(800 / itemsPerRow))}px` },
              draggable: false
            }
          ) : /* @__PURE__ */ jsx("div", { className: cn(
            "w-16 h-16 rounded-xl border-2 transition-all",
            isColored ? "bg-[#55C3FF] border-[#2E96CE]" : "bg-gray-200 border-gray-300",
            !isSolved && !isViewSolved && "hover:scale-105"
          ) })
        },
        itemIndex
      );
    }) }, rowIndex)) }),
    isSolved && /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        className: cn(
          "mt-6 rounded-2xl text-[20px] py-2 px-6 font-medium text-center relative",
          shouldShowCorrectAnswers || (solveResult == null ? void 0 : solveResult.correct) ? "bg-[#D8F6EF] text-[#17AF8C]" : "bg-[#FFE1DF] text-[#FF675E]"
        ),
        children: feedbackText
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: false,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers && correctColorCount !== null ? inverted ? t("types.coloring.eraseCount", { count: digits - correctColorCount }) : t("types.coloring.colorCount", { count: correctColorCount }) : void 0,
        disableSound
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !solveResult.correct,
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  ColoringStudent
};
//# sourceMappingURL=coloring-student.js.map
